import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import instance from "../../services/services";
import { DATA_LIST_URL, CATEGORY_LIST_URL } from "../../utils/urls";
import ProductSection from '../../components/Product/ProductSection';
import DefaultSelect from './DefaultSelect';
import PaginationSection from '../../components/Product/Pagination';
import { useLanguage } from '../../hooks/useLanguage';


function MainMore() {
    const { type, currentType } = useParams();
    const { t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false); // 탭 로딩 상태 추가
    const [offset, setOffset] = useState(1);
    const [categoryList, setCategoryList] = useState([]); // 카테고리 리스트
    const [activeTab, setActiveTab] = useState('all'); // 활성화된 탭

    // 페이지네이션을 위한 state 추가
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 30; // 한 페이지당 30개 상품
    const [totalCount, setTotalCount] = useState(0); // 전체 데이터 개수를 저장할 state 추가
    const [currentSort, setCurrentSort] = useState('regist_date'); // 정렬 상태 추가

    // 섹션 설정
    const sectionConfig = {
        new: {
            id: 'new',
            title: 'NEW ARRIVAL',
            showTabs: true
        },
        camera: {
            id: 'camera',
            title: 'CAMERA',
            showTabs: false
        },
        lens: {
            id: 'lens',
            title: 'LENS',
            showTabs: false
        },
        etc: {
            id: 'etc',
            title: 'ETC',
            showTabs: false
        },
        acc: {
            id: 'acc',
            title: 'ACCESSORY',
            showTabs: false
        }
    };


    // Swiper 옵션 설정
    const swiperOptions = {
        slidesPerView: 2,
        spaceBetween: 8,
        autoHeight: false,
        pagination: { clickable: true },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
        },
        grid: {
            rows: 6,
            fill: 'row'
        },
        breakpoints: {
            1280: {
                slidesPerView: 5,
                spaceBetween: 18,
            },
            768: {
                slidesPerView: 4,
                spaceBetween: 12,
            }
        }
    };

    // 카테고리 목록 가져오기
    useEffect(() => {
        const fetchCategoryList = async () => {
            try {
                const response = await instance.post(CATEGORY_LIST_URL);
                setCategoryList(response.data.data);
            } catch (error) {
                console.error("카테고리 목록 가져오기 실패", error);
            }
        };
        
        if (type === 'new') {
            fetchCategoryList();
        }
    }, [type]);

    // 데이터 가져오는 함수 - isTabChange 파라미터 추가
    const fetchProducts = useCallback(async (currentOffset, sortValue, categoryCode = "", isTabChange = false) => {
        try {
            if (isTabChange) {
                setTabLoading(true); // 탭 변경 시에는 탭 로딩만 활성화
            } else {
                setLoading(true); // 일반 로딩 활성화
            }

            const response = await instance.post(DATA_LIST_URL, {
                category_code: categoryCode,
                goods_type: type,
                main_kind: currentType,
                member_seq: "",
                offset: currentOffset,
                limit: 30,
                searchSort: sortValue
            });

            const totalItems = parseInt(response.data.data.cntRow);
            setTotalCount(totalItems);
            setProducts(response.data.data.data);
            
            if (isTabChange) {
                setTabLoading(false);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error("상품 데이터 가져오기 실패", error);
            if (isTabChange) {
                setTabLoading(false);
            } else {
                setLoading(false);
            }
        }
    }, [type, currentType]);

    // 초기 로드시에만 실행
    useEffect(() => {
        setOffset(1);
        fetchProducts(1, currentSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]); // type이 변경될 때만 실행

    // 정렬 변경 핸들러
    const handleSortChange = (value) => {
        setCurrentSort(value);
        setCurrentPage(1);
        fetchProducts(1, value); // 정렬 변경 시 직접 호출
    };

    // 탭 클릭 핸들러 - isTabChange 플래그 추가
    const handleTabClick = (tabId) => {
        setActiveTab(tabId);

        // 선택된 카테고리 찾기
        const selectedCategory = categoryList.find(category => category.category_seq === tabId);

        // category_code 값 결정
        const categoryCode = tabId === 'all' ? "" : selectedCategory?.category_code;

        // 탭 변경 시 첫 페이지로 이동
        setCurrentPage(1);

        // fetchProducts에 category_code와 isTabChange 플래그 전달
        fetchProducts(1, currentSort, categoryCode, true);
    };

    if (loading && offset === 1) {
        return <div className="loading">로딩 중...</div>;
    }

    const currentSection = sectionConfig[type];

    // beforeContent 컴포넌트 수정
    const BeforeContent = () => (
        <div className="auth_find">
            <div className="product_total">
            {t('product_total_count', { count: totalCount })}
            </div>
            <DefaultSelect 
                defaultValue={currentSort}
                onChange={handleSortChange}
            />
        </div>
    );

    // afterContent 컴포넌트에서 PaginationSection 사용
    const AfterContent = () => (
        <div className="pagination_wrap">
            <PaginationSection
                totalCount={totalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onArrow={handleArrowClick}
            />
        </div>
    );

    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchProducts(page, currentSort); // 현재 정렬 값 전달
        window.scrollTo(0, 0);
    };

    // 화살표 클릭 핸들러
    const handleArrowClick = (page) => {
        handlePageChange(page);
    };

    return (
        <div className="main_more">
            <ProductSection
                title={currentSection.title}
                products={products} // getCurrentPageProducts() 대신 전체 products 전달
                swiperOptions={swiperOptions}
                showTabs={currentSection.showTabs}
                categoryList={categoryList}
                activeTab={activeTab}
                onTabClick={handleTabClick}
                sectionId={currentSection.id}
                showMoreButton={false}
                beforeContent={<BeforeContent />}
                afterContent={<AfterContent />}
                useSwiper={false}
            />
            {/* 탭 로딩 상태 표시 */}
            {tabLoading && (
                <div className="tab-loading-overlay" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10
                }}>
                    <div className="loading">로딩 중...</div>
                </div>
            )}
            {loading && <div className="loading">로딩 중...</div>}
        </div>
    );
}

export default MainMore;